<template>
	<input
		:id="id"
		ref="autocomplete"
		type="text"
		:class="classname"
		:placeholder="placeholder"
		:disabled="disabled"
		v-bind="inputAttrs"
		:value="autocompleteText"
		@input="handleInput"
		@focus="onFocus"
		@blur="onBlur"
		@change="onChange"
		@keypress="onKeyPress"
		@keyup="onKeyUp"
	/>
</template>

<script>
const ADDRESS_COMPONENTS = {
	subpremise: 'short_name',
	street_number: 'short_name',
	route: 'long_name',
	locality: 'long_name',
	administrative_area_level_1: 'short_name',
	administrative_area_level_2: 'long_name',
	country: 'long_name',
	postal_code: 'short_name'
}

const UNSUPPORTED_RESTRICTION_COUNTRIES = ['XK']
const CITIES_TYPE = ['locality', 'administrative_area_level_3']
const REGIONS_TYPE = ['locality', 'sublocality', 'postal_code', 'country', 'administrative_area_level_1', 'administrative_area_level_2']
const BASIC_DATA_FIELDS = ['address_components', 'adr_address', 'alt_id', 'formatted_address', 'geometry', 'icon', 'id', 'name', 'business_status', 'photo', 'place_id', 'type', 'url', 'utc_offset_minutes', 'vicinity']

export default {
	name: 'GoogleAutocompleteInput',
	inheritAttrs: false,
	props: {
		id: {
			type: String,
			required: true
		},
		value: {
			type: [String, Number],
			default: null
		},
		classname: {
			type: [String, Array, Object],
			default: null
		},
		placeholder: {
			type: String,
			default: 'Start typing'
		},
		disabled: {
			type: Boolean,
			default: false
		},
		types: {
			type: String,
			default: 'address'
		},
		googleFields: {
			type: Array,
			default: () => BASIC_DATA_FIELDS
		},
		country: {
			type: [String, Array],
			default: null
		},
		enableGeolocation: {
			type: Boolean,
			default: false
		},
		geolocationOptions: {
			type: Object,
			default: null
		}
	},
	data () {
		return {
			autocomplete: null,
			autocompleteText: this.value ?? '',
			geolocation: {
				geocoder: null,
				loc: null,
				position: null
			},
			dropdownRepositionHandlers: [],
			repositionPending: false,
			isFocused: false,
			pacContainerObserver: null,
			activePacContainer: null
		}
	},
	computed: {
		inputAttrs () {
			const attrs = { ...this.$attrs }
			delete attrs.fields
			return attrs
		},
		effectiveFields () {
			if (Array.isArray(this.googleFields) && this.googleFields.length) {
				return this.googleFields
			}

			if (Array.isArray(this.$attrs.fields) && this.$attrs.fields.length) {
				return this.$attrs.fields
			}

			return BASIC_DATA_FIELDS
		}
	},
	watch: {
		value (newVal) {
			const normalizedValue = newVal ?? ''
			if (normalizedValue !== this.autocompleteText) {
				this.autocompleteText = normalizedValue
			}
		},
		autocompleteText (newVal, oldVal) {
			this.$emit('input-change', { newVal, oldVal }, this.id)
		},
		country (newVal) {
			if (!this.autocomplete) {
				return
			}

			this.autocomplete.setComponentRestrictions({
				country: this.normalizeCountryRestriction(newVal)
			})
		}
	},
	mounted () {
		const options = {}

		if (this.types) {
			options.types = [this.types]
		}

		if (this.country) {
			const restrictedCountry = this.normalizeCountryRestriction(this.country)
			if (restrictedCountry.length) {
				options.componentRestrictions = {
					country: restrictedCountry
				}
			}
		}

		this.autocomplete = new google.maps.places.Autocomplete(this.$refs.autocomplete, options)

		if (typeof this.autocomplete.setFields === 'function') {
			this.autocomplete.setFields(this.effectiveFields)
		}

		this.autocomplete.addListener('place_changed', this.onPlaceChanged)
	},
	beforeDestroy () {
		this.stopDropdownReposition()
		if (this.autocomplete && typeof google !== 'undefined' && google.maps?.event) {
			google.maps.event.clearInstanceListeners(this.autocomplete)
		}
	},
	methods: {
		normalizeCountryRestriction (value) {
			const codes = Array.isArray(value) ? value : (value ? [value] : [])
			return codes.filter(code => !UNSUPPORTED_RESTRICTION_COUNTRIES.includes(String(code).toUpperCase()))
		},
		handleInput (event) {
			this.autocompleteText = event.target.value
			this.$emit('input', this.autocompleteText)
			this.scheduleDropdownReposition()
		},
		onPlaceChanged () {
			const place = this.autocomplete.getPlace()

			if (!place.geometry) {
				this.$emit('no-results-found', place, this.id)
				return
			}

			if (place.address_components !== undefined) {
				this.$emit('placechanged', this.formatResult(place), place, this.id)
				this.autocompleteText = place.formatted_address || place.name || ''
				this.$emit('input', this.autocompleteText)
				this.onChange()
			}
		},
		onFocus () {
			this.isFocused = true
			this.biasAutocompleteLocation()
			this.$emit('focus')
			this.startDropdownReposition()
			this.$nextTick(() => {
				this.scheduleDropdownReposition()
				setTimeout(() => this.scheduleDropdownReposition(), 100)
				setTimeout(() => this.scheduleDropdownReposition(), 300)
			})
		},
		onBlur () {
			this.isFocused = false
			this.$emit('blur')
			this.stopDropdownReposition()
		},
		onChange () {
			this.$emit('change', this.autocompleteText)
		},
		onKeyPress (event) {
			this.$emit('keypress', event)
		},
		onKeyUp (event) {
			this.$emit('keyup', event)
		},
		clear () {
			this.autocompleteText = ''
		},
		focus () {
			this.$refs.autocomplete.focus()
		},
		blur () {
			this.$refs.autocomplete.blur()
		},
		update (value) {
			this.autocompleteText = value ?? ''
		},

		getPacContainer () {
			if (this.activePacContainer && document.body.contains(this.activePacContainer)) {
				const { display, visibility } = window.getComputedStyle(this.activePacContainer)
				if (display !== 'none' && visibility !== 'hidden') {
					return this.activePacContainer
				}
			}
			const containers = Array.from(document.querySelectorAll('.pac-container'))
			if (!containers.length) return null
			const visibleContainers = containers.filter(container => {
				const { display, visibility } = window.getComputedStyle(container)
				return display !== 'none' && visibility !== 'hidden'
			})
			if (visibleContainers.length) {
				this.activePacContainer = visibleContainers[0]
				return this.activePacContainer
			}
			this.activePacContainer = containers[0]
			return this.activePacContainer
		},

		getScrollableAncestors (element) {
			const ancestors = []
			let node = element?.parentElement
			while (node) {
				const { overflowY, overflowX } = window.getComputedStyle(node)
				if (overflowY === 'auto' || overflowY === 'scroll' || overflowX === 'auto' || overflowX === 'scroll') {
					ancestors.push(node)
				}
				if (node === document.body || node === document.documentElement) break
				node = node.parentElement
			}
			ancestors.push(document)
			ancestors.push(window)
			return ancestors
		},

		startDropdownReposition () {
			this.bindDropdownReposition()
			this.observePacContainer()
		},

		stopDropdownReposition () {
			this.unbindDropdownReposition()
			this.disconnectPacContainerObserver()
		},

		observePacContainer () {
			if (this.pacContainerObserver || typeof MutationObserver === 'undefined') return
			const isVisible = container => {
				if (!container) return false
				const { display, visibility } = window.getComputedStyle(container)
				return display !== 'none' && visibility !== 'hidden'
			}
			this.pacContainerObserver = new MutationObserver(mutations => {
				let activeContainer = null
				for (const mutation of mutations) {
					if (mutation.type === 'childList') {
						for (const node of mutation.addedNodes) {
							if (node.nodeType !== Node.ELEMENT_NODE) continue
							if (node.classList?.contains('pac-container') && isVisible(node)) {
								activeContainer = node
								break
							}
							if (typeof node.querySelector === 'function') {
								const nested = node.querySelector('.pac-container')
								if (nested && isVisible(nested)) {
									activeContainer = nested
									break
								}
							}
						}
					} else if (mutation.type === 'attributes') {
						const target = mutation.target
						if (target?.classList?.contains('pac-container') && isVisible(target)) {
							activeContainer = target
							break
						}
					}
					if (activeContainer) break
				}
				if (activeContainer && this.isFocused) {
					this.activePacContainer = activeContainer
					this.repositionDropdown()
				}
			})
			this.pacContainerObserver.observe(document.body, {
				childList: true,
				subtree: true,
				attributes: true,
				attributeFilter: ['style', 'class']
			})
		},

		disconnectPacContainerObserver () {
			if (!this.pacContainerObserver) return
			this.pacContainerObserver.disconnect()
			this.pacContainerObserver = null
		},

		bindDropdownReposition () {
			if (this.dropdownRepositionHandlers.length) return
			const boundReposition = this.scheduleDropdownReposition.bind(this)
			const targets = this.getScrollableAncestors(this.$refs.autocomplete)
			targets.forEach(target => {
				target.addEventListener('scroll', boundReposition, { passive: true })
				this.dropdownRepositionHandlers.push({ target, event: 'scroll', boundReposition })
			})
			window.addEventListener('resize', boundReposition, { passive: true })
			this.dropdownRepositionHandlers.push({ target: window, event: 'resize', boundReposition })
		},

		unbindDropdownReposition () {
			this.dropdownRepositionHandlers.forEach(({ target, event, boundReposition }) => {
				target.removeEventListener(event, boundReposition)
			})
			this.dropdownRepositionHandlers = []
		},

		scheduleDropdownReposition () {
			if (this.repositionPending || !this.$refs.autocomplete) return
			this.repositionPending = true
			requestAnimationFrame(() => {
				this.repositionPending = false
				this.repositionDropdown()
			})
		},

		repositionDropdown () {
			if (!this.$refs.autocomplete) return
			const container = this.getPacContainer()
			if (!container) return
			const { display, visibility } = window.getComputedStyle(container)
			if (display === 'none' || visibility === 'hidden') return
			this.activePacContainer = container
			if (container.parentElement !== document.body) {
				document.body.appendChild(container)
			}
			const rect = this.$refs.autocomplete.getBoundingClientRect()
			if (!rect.width || !rect.height) return
			container.style.setProperty('position', 'fixed', 'important')
			container.style.setProperty('top', `${rect.bottom}px`, 'important')
			container.style.setProperty('bottom', 'auto', 'important')
			container.style.setProperty('left', `${rect.left}px`, 'important')
			container.style.setProperty('right', 'auto', 'important')
			container.style.setProperty('width', `${rect.width}px`, 'important')
			container.style.setProperty('max-width', 'none', 'important')
			container.style.setProperty('margin', '0', 'important')
			container.style.setProperty('transform', 'none', 'important')
		},
		updateCoordinates (value) {
			if (!value || (!value.lat && !value.lng)) {
				return
			}

			if (!this.geolocation.geocoder) {
				this.geolocation.geocoder = new google.maps.Geocoder()
			}

			this.geolocation.geocoder.geocode({ location: value }, (results, status) => {
				if (status === 'OK') {
					results = this.filterGeocodeResultTypes(results)
					if (results[0]) {
						this.$emit('placechanged', this.formatResult(results[0]), results[0], this.id)
						this.update(results[0].formatted_address)
					} else {
						this.$emit('error', 'no result for provided coordinates')
					}
				} else {
					this.$emit('error', 'error getting address from coords')
				}
			})
		},
		geolocate () {
			this.updateGeolocation((geolocation) => {
				this.updateCoordinates(geolocation)
			})
		},
		updateGeolocation (callback = null) {
			if (!navigator.geolocation) {
				return
			}

			const options = this.geolocationOptions ? { ...this.geolocationOptions } : {}

			navigator.geolocation.getCurrentPosition(position => {
				const geolocation = {
					lat: position.coords.latitude,
					lng: position.coords.longitude
				}

				this.geolocation.loc = geolocation
				this.geolocation.position = position

				if (callback) {
					callback(geolocation, position)
				}
			}, err => {
				this.$emit('error', 'Cannot get Coordinates from navigator', err)
			}, options)
		},
		biasAutocompleteLocation () {
			if (!this.enableGeolocation) {
				return
			}

			this.updateGeolocation((geolocation, position) => {
				const circle = new google.maps.Circle({
					center: geolocation,
					radius: position.coords.accuracy
				})

				this.autocomplete.setBounds(circle.getBounds())
			})
		},
		formatResult (place) {
			const returnData = {}

			for (let i = 0; i < place.address_components.length; i++) {
				const addressType = place.address_components[i].types[0]

				if (ADDRESS_COMPONENTS[addressType]) {
					returnData[addressType] = place.address_components[i][ADDRESS_COMPONENTS[addressType]]
				}
			}

			returnData.latitude = place.geometry.location.lat()
			returnData.longitude = place.geometry.location.lng()

			return returnData
		},
		filterGeocodeResultTypes (results) {
			if (!results || !this.types) {
				return results
			}

			const output = []
			let types = [this.types]
			if (types.includes('(cities)')) {
				types = types.concat(CITIES_TYPE)
			}
			if (types.includes('(regions)')) {
				types = types.concat(REGIONS_TYPE)
			}

			for (const result of results) {
				for (const type of result.types) {
					if (types.includes(type)) {
						output.push(result)
						break
					}
				}
			}

			return output
		}
	}
}
</script>

